import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedProjectId = localStorage.getItem('active_project_id');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      loadProjects(savedProjectId);
    } else {
      setLoading(false);
    }
  }, []);

  const loadProjects = async (savedProjectId) => {
    try {
      const response = await api.get('/projects');

      // Handle new response format with plan_info
      const raw = response.data;
      const planData = raw?.plan_info || null;
      const projectsList = Array.isArray(raw?.projects)
        ? raw.projects
        : Array.isArray(raw)
          ? raw
          : [];

      setProjects(projectsList);
      // Normalize plan_info so "unlimited" coming from backend as null behaves correctly in UI
      const normalizedPlanInfo = planData
        ? {
            ...planData,
            max_projects:
              planData.max_projects === null || planData.max_projects === undefined
                ? Number.POSITIVE_INFINITY
                : planData.max_projects,
          }
        : null;
      setPlanInfo(normalizedPlanInfo);

      if (projectsList.length > 0) {
        const projectToActivate = savedProjectId
          ? projectsList.find(p => p.id === savedProjectId) || projectsList[0]
          : projectsList[0];

        setActiveProject(projectToActivate);
        localStorage.setItem('active_project_id', projectToActivate.id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setLoading(false);
    }
  };

  const switchProject = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setActiveProject(project);
      localStorage.setItem('active_project_id', projectId);
    }
  };

  const createProject = async (name, domain = '') => {
    try {
      const payload = { name };
      if (domain && domain.trim()) {
        payload.initial_domain = domain.trim().toLowerCase();
      }

      const response = await api.post('/projects', payload);
      const newProject = response.data;
      setProjects(prevProjects => [...prevProjects, newProject]);
      setActiveProject(newProject);
      localStorage.setItem('active_project_id', newProject.id);
      return newProject;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      // Load projects after login
      await loadProjects(null);
      return userData;
    } catch (error) {
      console.error('Login error in AuthContext:', error);
      throw error;
    }
  };

  const register = async (name, email, password, language = 'en') => {
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        language  // Pass language for email translations
      });

      // Backend returns success but no access token for email verification flow
      // User must verify email first, then login
      const { user: userData } = response.data;

      return userData;
    } catch (error) {
      console.error('Register error in AuthContext:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('active_project_id');
    setUser(null);
    setProjects([]);
    setActiveProject(null);
    setPlanInfo(null);
  };

  // setUser fonksiyonunu export et (login sayfası için gerekli)
  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      projects,
      activeProject,
      switchProject,
      createProject,
      loadProjects,
      setUser: updateUser,
      planInfo
    }}>
      {children}
    </AuthContext.Provider>
  );
};